import type { Signal } from "@preact/signals";
import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";

export const TextInputWithStyle = (
  inputStyle: JSX.CSSProperties,
  labelStyle: JSX.CSSProperties,
  autoFocus?: boolean,
) =>
({
  name,
  placeholder,
  signal,
  onSubmit,
}: {
  name?: string;
  placeholder?: string;
  signal: Signal<string>;
  onSubmit?: () => void;
}) => {
  let textareaRef: HTMLTextAreaElement | null = null;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (autoFocus && textareaRef) {
      textareaRef.focus();
    }
    // Detect mobile on mount
    setIsMobile(globalThis.innerWidth <= 768);

    const handleResize = () => {
      setIsMobile(globalThis.innerWidth <= 768);
    };
    globalThis.addEventListener("resize", handleResize);
    return () => globalThis.removeEventListener("resize", handleResize);
  }, []);

  const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto";
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px";
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const target = event.target as HTMLTextAreaElement;

    // On mobile, Enter should add newline (default behavior)
    if (isMobile && event.key === "Enter") {
      return; // Allow default behavior
    }

    // On desktop: Ctrl+Enter adds newline
    if (event.ctrlKey && event.key === "Enter") {
      event.preventDefault();
      const start = target.selectionStart;
      const end = target.selectionEnd;
      const newValue = signal.value.slice(0, start) + "\n" +
        signal.value.slice(end);
      signal.value = newValue;
      setTimeout(() => {
        target.selectionStart = target.selectionEnd = start + 1;
        autoResizeTextarea(target);
      }, 0);
    } // On desktop: Enter submits
    else if (
      !isMobile && event.key === "Enter" && !event.shiftKey && !event.metaKey
    ) {
      event.preventDefault();
      if (onSubmit && signal.value.trim()) {
        onSubmit();
        setTimeout(() => {
          autoResizeTextarea(target);
        }, 0);
      }
    }
  };

  return (
    <label style={labelStyle}>
      <div>{name}</div>
      <textarea
        ref={(el) => {
          textareaRef = el;
        }}
        style={{
          ...inputStyle,
          resize: "none",
          fontFamily: "inherit",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
        placeholder={placeholder}
        value={signal}
        onInput={(event) => {
          signal.value = (event.target as HTMLTextAreaElement).value;
          autoResizeTextarea(event.target as HTMLTextAreaElement);
        }}
        onKeyDown={handleKeyDown}
        rows={1}
      />
    </label>
  );
};

export const TextInput = TextInputWithStyle({}, {});

export const TriStateCheckbox = ({
  name,
  signal,
}: {
  name: string;
  signal: Signal<boolean | undefined>;
}) => {
  const onClick = () => {
    signal.value = signal.value === undefined
      ? true
      : signal.value === true
      ? false
      : undefined;
  };
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: <explanation>
    <label
      onClick={onClick}
      onKeyPress={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        cursor: "pointer",
      }}
    >
      <span
        tabindex={0}
        style={{
          margin: 3,
          width: 12,
          height: 12,
          border: "1px solid rgb(131 128 128)",
          borderRadius: 3,
          backgroundColor: "#333333",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {signal.value === true ? "✔" : signal.value === false ? "✖" : ""}
      </span>
      &nbsp;
      {name}
    </label>
  );
};

export const Checkbox = ({
  name,
  signal,
}: {
  name: string;
  signal: Signal<boolean>;
}) => (
  <label style={{ display: "block" }}>
    <input
      checked={signal}
      onClick={() => {
        signal.value = !signal.value;
      }}
      type="checkbox"
    />
    &nbsp;
    {name}
  </label>
);

export const NumberInput = ({
  name,
  signal,
  max,
  min,
}: {
  name: string;
  signal: Signal<number>;
  max: number;
  min: number;
}) => (
  <label style={{ display: "block" }}>
    {name}
    &nbsp;
    <input
      max={max}
      min={min}
      type="number"
      id={name}
      name={name}
      onInput={(event) => {
        signal.value = Number.parseInt(
          (event.target as HTMLInputElement).value,
        );
      }}
      value={signal.value}
    />
  </label>
);

export const ProgressBar = ({
  percentage,
  text,
  color,
}: {
  color: string;
  text: string;
  percentage: number;
}) => (
  <div>
    <span>
      {text}: {percentage}%
    </span>
    <div
      style={{
        width: "300px",
        border: "1px solid #ccc",
        borderRadius: "4px",
      }}
    >
      <div
        style={{
          width: `${percentage}%`,
          height: "10px",
          backgroundColor: color,
          borderRadius: "4px",
          transition: "width 0.3s ease",
        }}
      />
    </div>
  </div>
);

export const Select = ({
  name,
  options,
  signal,
}: {
  name: string;
  options: string[];
  signal: Signal<string>;
}) => (
  <label style={{ display: "block" }}>
    {name}
    &nbsp;
    <select
      value={signal.value}
      onChange={(event) => {
        signal.value = (event.target as HTMLSelectElement).value;
      }}
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </label>
);

export const useIsMobile = () => {
  const [width, setWidth] = useState(1024);
  useEffect(() => {
    setWidth(globalThis.innerWidth);
    const handleWindowSizeChange = () => {
      setWidth(globalThis.innerWidth);
    };
    globalThis.addEventListener("resize", handleWindowSizeChange);
    return () => {
      globalThis.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);
  return width <= 768;
};
